import { camelizeKeys } from 'humps';
import mime from 'mime';
import { normalize } from 'normalizr';
import {
  IdRef,
  InspectionScopeItemInterface,
  OriginalReportDeficiency,
  Report,
  ReportApplianceInformation,
  ReportAttachment,
  ReportDeficiency,
  ReportInformation,
  ReportLimitation,
  ReportNested,
  ReportRecord,
  ReportSubsystem,
  ReportSystem,
  ReportSystemGroup,
} from '../../types';
import { ReportSchema } from './schema';

interface Options {
  existingAttachments?: { [id: string]: ReportAttachment };
  captureException?: (error: string, data?: any) => void;
}

export const normalizeGraphqlReportResponse = (
  response: ReportNested,
  {
    existingAttachments = {},
    captureException = () => {},
  }: Options,
): Report | null => {
  const { entities, result } = normalize(camelizeKeys(response), ReportSchema);

  const {
    report,
    inspections,
    inspectionScopeItems,
    systemGroups,
    systems,
    subsystems,
    deficiencies,
    originalDeficiencies,
    limitations,
    informations,
    applianceInformations,
    locations,
    attachments,
  } = entities;

  if (report === undefined) {
    captureException('Report normalized doesn\'t have all required entities:', entities);
    return null;
  }

  const reportRecord: ReportRecord = {
    id: report[result].id,
    originalReportId: report[result].originalReportId,
    originalReport: report[result].originalReport,
    reportSystemIds: report[result].reportSystems,
    inspectionIds: Object.keys(inspections || {}),
    templateReportIds: report[result].templateReportIds,
    templateNames: report[result].templateNames,
    pdfDocumentUrl: report[result].pdfDocumentUrl,
    noDeficienciesAllowed: report[result]?.noDeficienciesAllowed || false,
    noLimitationsAllowed: report[result]?.noLimitationsAllowed || false,
    autoDeficiencyLocationRequired: report[result]?.autoDeficiencyLocationRequired || false,
    includePermitData: report[result]?.includePermitData || false,
    includeWideAnglePhotos: report[result]?.includeWideAnglePhotos || false,
    geotaggingRequired: report[result]?.geotaggingRequired || false,
    createdAt: report[result].createdAt,
    updatedAt: report[result].updatedAt,
    publishedAt: report[result].publishedAt,
    complianceText: report[result].complianceText,
  };

  const scopeItems: InspectionScopeItemInterface = (
    inspections && inspectionScopeItems
      ? Object.keys(inspections).reduce((acc: InspectionScopeItemInterface, inspectionId) => {
        const items = { ...acc };
        const inspection = inspections[inspectionId];
        (inspection?.inspectionScopeItems || []).forEach((id: IdRef) => {
          if (inspectionScopeItems[id]) items[id] = { ...inspectionScopeItems[id], inspectionId };
        });
        return items;
      }, {})
      : {}
  );

  const reportSystemGroups: { [id: string]: ReportSystemGroup } = systemGroups ?? {};

  const reportSystems: { [id: string]: ReportSystem } = systems ? Object.assign(
    {},
    ...Object.keys(systems).map((reportSystemId) => {
      const {
        reportSubsystems,
        reportAttachments,
        ...system
      } = systems[reportSystemId];

      return (
        {
          [system.id]: {
            ...system,
            reportSubsystemIds: reportSubsystems,
            reportAttachmentIds: reportAttachments || [],
          },
        }
      );
    }),
  ) : {};

  const reportSubsystems: { [id: string]: ReportSubsystem } = subsystems ? Object.assign(
    {},
    ...Object.keys(subsystems).map((reportSubsystemId) => {
      const {
        reportDeficiencies,
        reportInformations,
        reportLimitations,
        reportAttachments,
        ...subsystem
      } = subsystems[reportSubsystemId];

      return (
        {
          [subsystem.id]: {
            ...subsystem,
            reportDeficiencyIds: reportDeficiencies,
            reportInformationIds: reportInformations,
            reportLimitationIds: reportLimitations,
            reportAttachmentIds: reportAttachments,
          },
        }
      );
    }),
  ) : {};

  const originalReportDeficiencies: {
    [id: string]: OriginalReportDeficiency,
  } = originalDeficiencies ? Object.assign(
    {},
    ...Object.keys(originalDeficiencies).map((originalDeficiencyId) => {
      const {
        reportLocations,
        reportAttachments,
        ...originalDeficiency
      } = originalDeficiencies[originalDeficiencyId];

      return (
        {
          [originalDeficiency.id]: {
            ...originalDeficiency,
            reportLocationIds: reportLocations,
            reportAttachmentIds: reportAttachments,
          },
        }
      );
    }),
  ) : {};

  const reportDeficiencies: { [id: string]: ReportDeficiency } = deficiencies ? Object.assign(
    {},
    ...Object.keys(deficiencies).map((reportDeficiencyId) => {
      const {
        reportLocations,
        reportAttachments,
        originalReportDeficiency,
        ...deficiency
      } = deficiencies[reportDeficiencyId];

      return (
        {
          [deficiency.id]: {
            ...deficiency,
            reportLocationIds: reportLocations,
            reportAttachmentIds: reportAttachments,
            originalReportDeficiencyId: originalReportDeficiency,
          },
        }
      );
    }),
  ) : {};

  const reportLimitations: { [id: string]: ReportLimitation } = limitations ? Object.assign(
    {},
    ...Object.keys(limitations).map((reportLimitationId) => {
      const {
        reportLocations,
        reportAttachments,
        ...limitation
      } = limitations[reportLimitationId];

      return (
        {
          [limitation.id]: {
            ...limitation,
            reportLocationIds: reportLocations,
            reportAttachmentIds: reportAttachments,
          },
        }
      );
    }),
  ) : {};

  const reportInformations: {
    [id: string]: ReportInformation
  } = informations ? Object.assign(
    {},
    ...Object.keys(informations).map((reportInformationId) => {
      const {
        reportLocations: reportLocationIds,
        reportAttachments: reportAttachmentIds,
        reportApplianceInformation: reportApplianceInformationId,
        catalogInformation,
        tags,
        ...information
      } = informations[reportInformationId];

      return (
        {
          [information.id]: {
            ...information,
            tags: tags ? JSON.parse(tags) : {},
            catlogInformationAutoDeficiencyCondition: (
              catalogInformation?.autoDeficiencyCondition
            ),
            catlogInformationAutoDeficiencyMediaRequired: (
              catalogInformation?.autoDeficiencyMediaRequired
            ),
            catalogInformationVisualAidTitle: (
              catalogInformation?.visualAidTitle
            ),
            catalogInformationVisualAidKbaUrl: (
              catalogInformation?.visualAidKbaUrl
            ),
            catalogInformationVisualAidImageUrl: (
              catalogInformation?.visualAidImageUrl
            ),
            reportApplianceInformationId,
            reportLocationIds,
            reportAttachmentIds,
          },
        }
      );
    }),
  ) : {};

  const reportApplianceInformations: {
    [id: string]: ReportApplianceInformation
  } = applianceInformations ? Object.assign(
    {},
    ...Object.keys(applianceInformations).map((applianceInformationId) => {
      const {
        reportAttachments: reportAttachmentIds,
        ...applianceInformation
      } = applianceInformations[applianceInformationId];

      return (
        {
          [applianceInformation.id]: {
            ...applianceInformation,
            reportAttachmentIds,
          },
        }
      );
    }),
  ) : {};

  const reportAttachments: {
    [id: string]: ReportAttachment
  } = attachments ? Object.assign(
    {},
    ...Object.keys(attachments).map((reportAttachmentId) => {
      const attachment = attachments[reportAttachmentId];

      const metadata = JSON.parse(attachment.metadata);

      const contentType = mime.getType(attachment.fileName?.split('.')?.pop());

      const resizedUrls = (attachment?.resizedUrls || []).reduce(
        (acc: any, { width, url }: any) => {
          acc[width] = url;
          return acc;
        }, {},
      );

      const rotatedUrls = (attachment?.rotatedUrls || []).reduce(
        (acc: any, { degree, url }: any) => {
          acc[degree] = url;
          return acc;
        }, {},
      );

      return (
        {
          [reportAttachmentId]: {
            ...existingAttachments[reportAttachmentId],
            ...attachment,
            resizedUrls,
            rotatedUrls,
            mediaType: contentType?.includes('pdf') ? 'pdf' : attachment.mediaType,
            metadata,
          },
        }
      );
    }),
  ) : {};

  return {
    report: reportRecord,
    inspectionScopeItems: scopeItems,
    systemGroups: reportSystemGroups,
    systems: reportSystems,
    subsystems: reportSubsystems,
    deficiencies: reportDeficiencies,
    originalReportDeficiencies,
    limitations: reportLimitations,
    informations: reportInformations,
    applianceInformations: reportApplianceInformations,
    locations: locations || {},
    attachments: reportAttachments,
    updatedAt: reportRecord.updatedAt,
  };
};

export * from './schema';
